import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, Trash2, UserSquare2 } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { EmptyState } from "@/components/common/EmptyState";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";

export default function AdminMembers() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchMembers = () => {
    api.get("/users/members", { params: { q: q || undefined } })
      .then((res) => setMembers(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(fetchMembers, 250);
    return () => clearTimeout(t);
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await api.delete(`/users/members/${deleteTarget.id}`);
      toast.success(`Member '${deleteTarget.name}' deleted permanently (${res.data.applications_deleted} application(s) removed)`);
      setDeleteTarget(null);
      fetchMembers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete member");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight">Members</h1>
        <p className="text-muted-foreground mt-1 text-sm">Every member is identified by their mobile number. One member can have multiple applications.</p>
      </div>

      <div className="relative max-w-md">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input data-testid="members-search-input" placeholder="Search by name or mobile..." className="pl-9 bg-white" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>

      {loading ? (
        <div className="space-y-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-14 rounded-xl" />)}</div>
      ) : members.length === 0 ? (
        <Card className="rounded-xl border-border shadow-none">
          <EmptyState icon={UserSquare2} title="No members found" description="Members are created when they apply for a job or save a resume." testId="members-empty" />
        </Card>
      ) : (
        <Card className="rounded-xl border-border shadow-none overflow-hidden">
          <div className="overflow-x-auto">
            <Table data-testid="admin-members-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Member</TableHead>
                  <TableHead>Mobile</TableHead>
                  <TableHead>Applications</TableHead>
                  <TableHead>Resume</TableHead>
                  <TableHead>Joined</TableHead>
                  {isSuperAdmin && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {members.map((m) => (
                  <TableRow key={m.id} className="hover:bg-muted/60" data-testid="admin-member-row">
                    <TableCell>
                      <div className="flex items-center gap-2.5">
                        <span className="h-8 w-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-semibold">
                          {(m.name || "?").charAt(0).toUpperCase()}
                        </span>
                        <p className="font-medium">{m.name}</p>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-sm">{m.mobile}</TableCell>
                    <TableCell className="font-mono text-sm">{m.applications_count}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`rounded-full text-xs ${m.has_resume ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-muted text-muted-foreground"}`}>
                        {m.has_resume ? "Yes" : "No"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">{formatDate(m.created_at)}</TableCell>
                    {isSuperAdmin && (
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground hover:text-red-600 hover:bg-red-50"
                          onClick={() => setDeleteTarget(m)}
                          aria-label={`Delete member ${m.name}`}
                          data-testid="member-delete-button"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="member-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently delete this member?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget && (
                <>
                  <span className="font-medium text-foreground">{deleteTarget.name}</span> ({deleteTarget.mobile}) will be permanently deleted along with{" "}
                  <span className="font-medium text-foreground">{deleteTarget.applications_count} application(s)</span>, their resume and related activity history.
                  Job posts are not affected. This action cannot be undone.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="member-delete-cancel" disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="member-delete-confirm"
              disabled={deleting}
              onClick={(e) => { e.preventDefault(); handleDelete(); }}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {deleting ? "Deleting..." : "Delete Permanently"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
